import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import Navbar from './Community/Navbar/Navbar';
import UploadPopup from "./Community/AddPost/AddPost";
import Layout from "./Community/Layout";
import { useUser } from "./hooks/useUser";
import HomePage from "./Community/HomePage";
import PostDetail from "./Community/Post/PostDetail";
import ProfileView from "./Community/UserProfile/ProfileView";
import Toast from "./components/Toast/Toast";
import BlockedAccess from "./Community/Components/AccessPage/blockedAccess";
import LoadingScreen from "./Community/Components/LoadingScreen/LoadingScreen";
import UsernamePrompt from "./Community/Components/UsernamePrompt/UsernamePrompt";
import { setProjectInfo } from "./redux/projectInfoSlice";
import qs from "query-string";
import "./App.css";
import { loadCustomFont } from "./utils/loadFont";
import ToastProvider from "./components/Toast/ToastProvider";

// Memoized HomePage wrapper to prevent unnecessary rerenders
const MemoizedHomePage = React.memo(({ handleAddPostClick }) => (
  <HomePage handleAddPostClick={handleAddPostClick} />
));

function AppContent() {
  const dispatch = useDispatch();
  const { fetchUsersByIds } = useUser();
  const { community_block_post_add, community_blocked } = useSelector((state) => state.projectInfo);
  const users = useSelector((state) => state.users.entities);

  const [showPopup, setShowPopup] = useState(false);
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "error" });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Refs for preventing multiple calls
  const validateCalledRef = useRef(false);
  const userDataFetchedRef = useRef(false);
  const hasTokenInUrl = useRef(false);

  // Initialize currentUserId from localStorage on mount
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      setCurrentUserId(userId);
    }
  }, []);

  const token = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlToken = queryParams.get('token');
    if (urlToken) {
      hasTokenInUrl.current = true;
      return urlToken;
    }
    return localStorage.getItem("token");
  }, []);

  // Memoized user data
  const user = useMemo(() =>
    users[currentUserId] || null,
    [users, currentUserId]
  );


  const hasUsername = useMemo(() =>
    user?.user_name && user.user_name.trim() !== "",
    [user?.user_name]
  );

  useEffect(() => {
    const initializeApp = async () => {
      // Prevent multiple initialization calls
      if (validateCalledRef.current) return;

      // Redirect to login if no token
      if (!token) {
        window.location.href = "/login";
        return;
      }

      validateCalledRef.current = true;
      setIsLoading(true);

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/token/validate`,
          {},
          {
            headers: {
              "access-token": token,
              token,
              "Content-Type": "application/json",
            },
            timeout: 10000, // 10 second timeout
          }
        );

        if (response?.data) {
          const {
            access_token: accessToken,
            uid,
            pubid,
            dev_id,
            channelid,
            config_data,
            community_block_comment_add,
            community_block_post_add,
            community_blocked,
            device_type,
            country_code
          } = response.data;
          const app_id = response.data.app_id
          const logo = config_data?.logo;

          // Handle the font loading
          try {
            const fontFamily = config_data?.font;
            const fontFile = `${fontFamily}.ttf`;

            await loadCustomFont({ fontFamily, fontFile });
          } catch (fontError) {
            console.error('Error loading custom font:', fontError);
          }

          // Dispatch project info to Redux
          dispatch(setProjectInfo({
            accessToken,
            uid,
            pubid,
            dev_id,
            channelid,
            logo,
            app_id: String(app_id),
            community_block_comment_add,
            community_block_post_add,
            community_blocked,
            device_type,
            country_code
          }));

          // Store token and user ID
          localStorage.setItem("token", token);

          // Update currentUserId state immediately
          setCurrentUserId(uid);

          // Clean up URL if token came from query params
          if (hasTokenInUrl.current) {
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            window.history.replaceState({}, document.title, url.toString());
          }
          const sessionId = localStorage.getItem("sessionId")
          const eventAccessToken = accessToken;
          let ctimestamp = Date.now().toString();
          let ctime = ctimestamp.slice(0, 10);
          const isSubscriptionRequired = config_data?.community_subscription_required === "true";
          if (isSubscriptionRequired) {
            const communityRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/subscription/active?is_community=true`, {
              headers: {
                uid: uid,
                pubid: pubid,
                channelid: channelid,
                device_id: dev_id,
                device_type: device_type,
                country_code: country_code
              }
            });
            const communitySubs = communityRes?.data?.data || [];
            const requiredSubIds = communitySubs.map(sub => sub.subscription_id);

            // Fetch user subscriptions
            const userRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/subscription/user`, {
              headers: {
                "Content-Type": "multipart/form-data",
                "Access-Control-Allow-Origin": true,
                crossorigin: true,
                "access-token": token,
                pubid: pubid,
                channelid: channelid,
                uid: uid,
                device_type: "web",
                dev_id: dev_id
              },
            });
            const userSubs = userRes?.data?.data || [];
            const userSubIds = userSubs.map(sub => sub.sub_id);

            // Check if user has any required subscription
            const hasRequired = requiredSubIds.some(id => userSubIds.includes(id));
            if (!hasRequired) {
              window.location.href = "/checkout";
              return;
            }
          }
          const data = {
            session_id: sessionId,
            user_id: uid,
            device_id: dev_id,
            publisherid: pubid,
            app_id: String(app_id || 666),
            channel_id: channelid,
            event_type: "POP13",
            timestamp: ctime,
          };
          const customConfig = {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Access-Control-Allow-Origin": true,
              crossorigin: true,
              "access-token": eventAccessToken,
            },
          };
          try {
            await axios.post(
              "https://analytics.poppo.tv/event",
              qs.stringify(data),
              customConfig
            );

          } catch (eventError) {
            console.error("Error sending analytics event:", eventError);
          }
          setIsInitialized(true);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {

        localStorage.removeItem("token");
        localStorage.removeItem("currentUser");
        window.location.href = "/login";
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [dispatch, token]);

  useEffect(() => {
    const fetchUserData = async () => {
      // Prevent multiple fetch calls
      if (!currentUserId || userDataFetchedRef.current || !isInitialized) {

        return;
      }
      userDataFetchedRef.current = true;
      setIsLoading(true);
      try {

        // Parallel fetch for better performance
        await Promise.all([
          fetchUsersByIds([currentUserId]),
        ]);

        setIsUserDataFetched(true);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setToast({
          show: true,
          message: "Failed to load user data. Please refresh the page.",
          type: "error"
        });
        // Reset flag to allow retry
        userDataFetchedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUserId, fetchUsersByIds, isInitialized]);


  // Check username requirement after user data is loaded
  useEffect(() => {
    if (isUserDataFetched && user && !hasUsername && !showUsernamePrompt) {
      setShowUsernamePrompt(true);
    }
  }, [isUserDataFetched, user, hasUsername, showUsernamePrompt]);

  // Memoized callback functions
  const hideUsernamePrompt = useCallback(() => {
    setShowUsernamePrompt(false);
  }, []);

  const handleAddPostClick = useCallback(() => {
    if (community_block_post_add) {
      setToast({
        show: true,
        message: "You are restricted from creating new posts in this community.",
        type: "error"
      });
    } else {
      setShowPopup(true);
    }
  }, [community_block_post_add]);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);

  const closePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  // Loading states
  if (isLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  // User blocked state
  if (isInitialized && community_blocked) {
    return <BlockedAccess />;
  }

  if (!isInitialized) {
    return <LoadingScreen message="Initializing application..." />;
  }

  // Only redirect if we have no token
  if (!token) {
    setTimeout(() => {
      window.location.href = "/login";
    }, 0);
    return <LoadingScreen message="Redirecting to login..." />;
  }

  return (
    <div className="App" style={{ backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
      <BrowserRouter>
        <Navbar />
        <ToastProvider />
        <div className="app-main">
          {/* Toast notifications */}
          {toast.show && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={hideToast}
            />
          )}

          {/* Upload popup */}
          {showPopup && (
            <UploadPopup onClose={closePopup} />
          )}

          {/* Username prompt */}
          {showUsernamePrompt && currentUserId && (
            <UsernamePrompt
              userId={currentUserId}
              onClose={hideUsernamePrompt}
              token={token}
            />
          )}

          {/* Show loading if currentUserId is not available yet */}
          {!currentUserId ? (
            <LoadingScreen message="Authenticating..." />
          ) : (
            /* Main routes */
            <Routes>
              <Route path="/community" element={<Layout />}>
                <Route
                  index
                  element={<MemoizedHomePage handleAddPostClick={handleAddPostClick} />}
                />
                <Route
                  path="posts/:vanityUrl"
                  element={<PostDetail />}
                />
                <Route
                  path="profiles/:userId"
                  element={<ProfileView />}
                />
                <Route
                  path="categories/:categoryId"
                  element={<MemoizedHomePage handleAddPostClick={handleAddPostClick} />}
                />
                {/* Redirect invalid community routes */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          )}
        </div>
      </BrowserRouter>
    </div>
  );
}

export default AppContent;